/**
 * Admin ⌘K search — the shared half of HQ's settings search (PR #3034), so
 * every internal app can search its own Settings / admin pages from the
 * palette with one line of config.
 *
 *   searchable: isAdmin ? [settingsSearchKind(SETTINGS_NAV), ...] : []
 *
 * The host keeps ONE list of its admin pages (the same list its sidebar
 * renders) and hands it here; `scoreMatch` + `rankMatches` are exported too so
 * a host's subpage kinds (templates, agents, teams) rank the same way.
 * Pure apart from `cachedList`'s fetch-once cache.
 */

import type { SearchableHit, SearchableKind } from '../types'

/** One admin page, as the sidebar and the palette both read it. */
export interface SettingsNavItem {
  key: string
  label: string
  href: string
  /** Words an admin would type for this page that its label does not say. */
  keywords?: string
}

export interface MatchCandidate {
  title: string
  /** Extra words the row also answers to (keywords, slug, team name). */
  extra?: string
}

/**
 * How well `candidate` answers `query`, lower is better, or null when it does
 * not. Every word of the query has to appear somewhere in the title or extra
 * words, so "team pa" finds Team Pages; a title that starts with the query
 * (0) outranks one that only contains it (1), which outranks a match found
 * only in the extra words (2).
 */
export function scoreMatch(query: string, candidate: MatchCandidate): number | null {
  const q = query.trim().toLowerCase()
  if (!q) {
    return null
  }
  const title = candidate.title.toLowerCase()
  const haystack = `${title} ${(candidate.extra ?? '').toLowerCase()}`
  if (!q.split(/\s+/).every((word) => haystack.includes(word))) {
    return null
  }
  if (title.startsWith(q)) {
    return 0
  }
  if (title.includes(q)) {
    return 1
  }
  return 2
}

/** The rows that match `query`, best first; ties keep the input order. */
export function rankMatches<T>(query: string, rows: readonly T[], toCandidate: (row: T) => MatchCandidate): T[] {
  return rows
    .map((row, index) => ({ row, index, score: scoreMatch(query, toCandidate(row)) }))
    .filter((r): r is { row: T; index: number; score: number } => r.score !== null)
    .sort((a, b) => a.score - b.score || a.index - b.index)
    .map((r) => r.row)
}

/** `nav` rows that answer `query`, shaped as palette hits. "settings" always
 *  matches, so typing the word lists every page. */
export function settingsHits(query: string, nav: readonly SettingsNavItem[], meta = 'Settings'): SearchableHit[] {
  return rankMatches(query, nav, (item) => ({ title: item.label, extra: `settings ${item.keywords ?? ''}` })).map(
    (item) => ({ id: `settings-${item.key}`, title: item.label, meta, href: item.href }),
  )
}

export interface SettingsSearchKindOptions {
  /** Section header. Default 'SETTINGS'. */
  label?: string
  /** Right-aligned label on each row. Default 'Settings'. */
  meta?: string
  /** Rows shown. Default 5, the palette's own default. */
  max?: number
}

/**
 * The SETTINGS section for a host's `searchable`. Gate it on the host's own
 * admin check — the palette shows whatever kinds it is given. Matching is
 * local, so it always answers inside the palette's per-kind timeout.
 */
export function settingsSearchKind(
  nav: readonly SettingsNavItem[],
  opts: SettingsSearchKindOptions = {},
): SearchableKind {
  return {
    kind: 'settings',
    label: opts.label ?? 'SETTINGS',
    ...(opts.max !== undefined ? { max: opts.max } : {}),
    search: async (query) => settingsHits(query, nav, opts.meta),
  }
}

export const CACHED_LIST_TTL_MS = 60_000

/**
 * A small admin list (templates, agents, teams) loaded once and reused across
 * keystrokes for a subpage kind, reloaded after `ttlMs`. Call the loader when
 * the top bar mounts so the first keystroke reads from cache instead of
 * racing the palette's 1.5s per-kind timeout. Loads without the per-keystroke
 * abort signal, so one cancelled search cannot poison the cache; a failed
 * load resolves to [] and is retried after the TTL. Returns the loader and an
 * `invalidate` for hosts that know the list changed.
 */
export function cachedList<T>(load: () => Promise<T[]>, ttlMs = CACHED_LIST_TTL_MS, now: () => number = Date.now) {
  let entry: { at: number; rows: Promise<T[]> } | null = null
  const get = () => {
    if (!entry || now() - entry.at > ttlMs) {
      entry = { at: now(), rows: load().catch(() => [] as T[]) }
    }
    return entry.rows
  }
  const invalidate = () => {
    entry = null
  }
  return Object.assign(get, { invalidate })
}

/** `cachedList` over a same-origin JSON GET that returns an array. */
export function cachedJsonList<T>(url: string, ttlMs = CACHED_LIST_TTL_MS) {
  return cachedList<T>(
    () => fetch(url, { credentials: 'same-origin' }).then((r) => (r.ok ? (r.json() as Promise<T[]>) : [])),
    ttlMs,
  )
}
